import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router";
import { ArrowLeft, Download01, Edit01, Trash01, Share01, Move } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { PdfViewer } from "@/components/application/pdf-viewer/pdf-viewer";
import { documentService } from "@/data/mock-documents";
import { documentVersionService } from "@/data/mock-document-versions";
import { containerService } from "@/data/mock-containers";
import type { DocumentFile } from "@/types/documents";
import type { DocumentVersion } from "@/types/container";
import { useTranslation } from "react-i18next";
import { Modal, ModalOverlay, Dialog } from "@/components/application/modals/modal";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";

export const ShipperDocumentDetailPage = () => {
    const { documentId } = useParams<{ documentId: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation("pages:documents");

    const [document, setDocument] = useState<DocumentFile | null>(null);
    const [versions, setVersions] = useState<DocumentVersion[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedVersion, setSelectedVersion] = useState<DocumentVersion | null>(null);

    // Modals
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDownloadModal, setShowDownloadModal] = useState(false);

    // Form states
    const [newName, setNewName] = useState("");
    const [selectedContainerId, setSelectedContainerId] = useState("");

    useEffect(() => {
        if (!documentId) return;

        const doc = documentService.getById(documentId) as DocumentFile | undefined;
        if (doc) {
            setDocument(doc);
            setNewName(doc.name);
            const docVersions = documentVersionService.getByDocumentId(documentId);
            setVersions(docVersions);
            if (docVersions.length > 0) {
                setSelectedVersion(docVersions[0]);
            }
        }
    }, [documentId]);

    const container = document?.containerId
        ? containerService.getById(document.containerId)
        : null;

    const isPdf = document?.mimeType === "application/pdf";
    const fileUrl = selectedVersion
        ? `/document-review-test.pdf` // In produzione, usare URL versione specifica
        : document?.fileUrl || "/document-review-test.pdf";

    const handleRename = () => {
        if (!document || !newName.trim()) return;
        const updated = documentService.rename(document.id, newName.trim());
        if (updated) {
            setDocument(updated as DocumentFile);
            setShowRenameModal(false);
        }
    };

    const handleMove = () => {
        if (!document || !selectedContainerId) return;
        const updated = documentService.move(document.id, selectedContainerId);
        if (updated) {
            setDocument(updated as DocumentFile);
            setShowMoveModal(false);
            navigate("/shipper/documents");
        }
    };

    const handleShare = () => {
        if (!document) return;
        // Placeholder - implementare logica condivisione
        documentService.share(document.id, {
            email: "",
            permission: "view",
        });
        setShowShareModal(false);
    };

    const handleDelete = () => {
        if (!document) return;
        documentService.delete(document.id);
        setShowDeleteModal(false);
        navigate("/shipper/documents");
    };

    const handleDownload = () => {
        if (!document) return;
        if (isPdf) {
            // Per PDF, usa il viewer
            return;
        }
        // Per altri formati, mostra modale conferma
        setShowDownloadModal(true);
    };

    const confirmDownload = () => {
        if (!document) return;
        // Simula download
        const link = window.document.createElement("a");
        link.href = document.fileUrl;
        link.download = document.name;
        link.click();
        setShowDownloadModal(false);
    };

    const containerOptions = containerService
        .getAll()
        .map(c => ({ id: c.id, label: c.containerNumber }));

    if (!document) {
        return (
            <div className="text-center py-12">
                <p className="text-tertiary">{t("drive.detail.notFound")}</p>
                <Link to="/shipper/documents" className="text-brand-600 hover:text-brand-700 mt-4 inline-block">
                    {t("drive.detail.backToDocuments")}
                </Link>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Link to="/shipper/documents" className="text-tertiary hover:text-primary">
                        <ArrowLeft className="size-5" />
                    </Link>
                    <div>
                        <h1 className="text-xl font-semibold text-primary">{document.name}</h1>
                        <p className="text-sm text-tertiary capitalize">
                            {document.type.replace(/_/g, " ")}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button color="secondary" size="sm" onClick={handleDownload} iconLeading={<Download01 className="size-4" />}>
                        {t("drive.detail.download")}
                    </Button>
                    <Button color="secondary" size="sm" onClick={() => setShowRenameModal(true)} iconLeading={<Edit01 className="size-4" />}>
                        {t("drive.document.actions.rename")}
                    </Button>
                    <Button color="secondary" size="sm" onClick={() => setShowMoveModal(true)} iconLeading={<Move className="size-4" />}>
                        {t("drive.document.actions.move")}
                    </Button>
                    <Button color="secondary" size="sm" onClick={() => setShowShareModal(true)} iconLeading={<Share01 className="size-4" />}>
                        {t("drive.document.actions.share")}
                    </Button>
                    <Button color="secondary" size="sm" onClick={() => setShowDeleteModal(true)} iconLeading={<Trash01 className="size-4" />}>
                        {t("drive.document.actions.delete")}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content - PDF Viewer */}
                <div className="lg:col-span-2">
                    <div className="rounded-xl bg-primary shadow-xs ring-1 ring-secondary overflow-hidden">
                        {isPdf ? (
                            <div className="h-[800px]">
                                <PdfViewer
                                    fileUrl={fileUrl}
                                    polygons={[]}
                                    currentPage={currentPage}
                                    onPageChange={setCurrentPage}
                                    hideAddRectangle={true}
                                />
                            </div>
                        ) : (
                            <div className="p-12 text-center">
                                <p className="text-tertiary mb-4">
                                    {t("drive.detail.previewNotAvailable")}
                                </p>
                                <Button onClick={handleDownload} iconLeading={<Download01 className="size-4" />}>
                                    {t("drive.detail.download")}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Container Info */}
                    {container && (
                        <div className="rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary">
                            <h2 className="text-sm font-semibold text-primary mb-3">
                                {t("drive.detail.containerInfo")}
                            </h2>
                            <div className="space-y-2 text-sm">
                                <div>
                                    <span className="text-tertiary">Container: </span>
                                    <span className="text-primary font-medium">
                                        {container.containerNumber}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-tertiary">Route: </span>
                                    <span className="text-primary">
                                        {container.route.from} → {container.route.to}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-tertiary">Status: </span>
                                    <span className="text-primary">{container.status}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Versions */}
                    {versions.length > 0 && (
                        <div className="rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary">
                            <h2 className="text-sm font-semibold text-primary mb-3">
                                {t("drive.detail.versions")}
                            </h2>
                            <div className="space-y-2">
                                {versions.map((version) => (
                                    <button
                                        key={version.id}
                                        onClick={() => setSelectedVersion(version)}
                                        className={`w-full text-left p-2 rounded-lg text-sm transition-colors ${selectedVersion?.id === version.id
                                            ? "bg-active text-primary"
                                            : "hover:bg-primary_hover text-tertiary"
                                            }`}
                                    >
                                        <div className="font-medium">
                                            {t("drive.detail.currentVersion")} {version.version}
                                        </div>
                                        <div className="text-xs text-tertiary mt-1">
                                            {new Date(version.uploadedAt).toLocaleString()}
                                        </div>
                                        <div className="text-xs text-tertiary">
                                            {version.uploadedBy.name}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Rename Modal */}
            {showRenameModal && (
                <ModalOverlay isOpen={showRenameModal} onOpenChange={setShowRenameModal}>
                    <Modal>
                        <Dialog className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md w-full mx-auto flex flex-col">
                            <h2 className="text-lg font-semibold text-primary mb-4">
                                {t("drive.document.rename.title")}
                            </h2>
                            <div className="space-y-4 w-full">
                                <Input
                                    label={t("drive.document.rename.label")}
                                    value={newName}
                                    onChange={setNewName}
                                />
                                <div className="flex justify-end gap-2">
                                    <Button color="secondary" onClick={() => setShowRenameModal(false)}>
                                        {t("drive.document.rename.cancel")}
                                    </Button>
                                    <Button onClick={handleRename}>
                                        {t("drive.document.rename.save")}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Move Modal */}
            {showMoveModal && (
                <ModalOverlay isOpen={showMoveModal} onOpenChange={setShowMoveModal}>
                    <Modal>
                        <Dialog className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md w-full mx-auto flex flex-col">
                            <h2 className="text-lg font-semibold text-primary mb-4">
                                {t("drive.document.move.title")}
                            </h2>
                            <div className="space-y-4 w-full">
                                <Select
                                    label={t("drive.document.move.label")}
                                    selectedKey={selectedContainerId}
                                    onSelectionChange={(key) => setSelectedContainerId(key as string)}
                                    items={containerOptions}
                                >
                                    {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                                </Select>
                                <div className="flex justify-end gap-2">
                                    <Button color="secondary" onClick={() => setShowMoveModal(false)}>
                                        {t("drive.document.move.cancel")}
                                    </Button>
                                    <Button onClick={handleMove}>
                                        {t("drive.document.move.save")}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Share Modal */}
            {showShareModal && (
                <ModalOverlay isOpen={showShareModal} onOpenChange={setShowShareModal}>
                    <Modal>
                        <Dialog className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md w-full mx-auto flex flex-col">
                            <h2 className="text-lg font-semibold text-primary mb-4">
                                {t("drive.document.share.title")}
                            </h2>
                            <div className="space-y-4 w-full">
                                <Input
                                    label={t("drive.document.share.email")}
                                    type="email"
                                    placeholder="email@example.com"
                                />
                                <Select
                                    label={t("drive.document.share.permission")}
                                    selectedKey="view"
                                    items={[
                                        { id: "view", label: t("drive.document.share.view") },
                                        { id: "edit", label: t("drive.document.share.edit") },
                                        { id: "comment", label: t("drive.document.share.comment") },
                                    ]}
                                >
                                    {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                                </Select>
                                <div className="flex justify-end gap-2">
                                    <Button color="secondary" onClick={() => setShowShareModal(false)}>
                                        {t("drive.document.share.cancel")}
                                    </Button>
                                    <Button onClick={handleShare}>
                                        {t("drive.document.share.send")}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Delete Modal */}
            {showDeleteModal && (
                <ModalOverlay isOpen={showDeleteModal} onOpenChange={setShowDeleteModal}>
                    <Modal>
                        <Dialog className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md w-full mx-auto flex flex-col">
                            <h2 className="text-lg font-semibold text-primary mb-4">
                                {t("drive.document.delete.title")}
                            </h2>
                            <div className="space-y-4 w-full">
                                <p className="text-tertiary">{t("drive.document.delete.message")}</p>
                                <div className="flex justify-end gap-2">
                                    <Button color="secondary" onClick={() => setShowDeleteModal(false)}>
                                        {t("drive.document.delete.cancel")}
                                    </Button>
                                    <Button color="primary-destructive" onClick={handleDelete}>
                                        {t("drive.document.delete.confirm")}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Download Modal */}
            {showDownloadModal && (
                <ModalOverlay isOpen={showDownloadModal} onOpenChange={setShowDownloadModal}>
                    <Modal>
                        <Dialog className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md w-full mx-auto flex flex-col">
                            <h2 className="text-lg font-semibold text-primary mb-4">
                                {t("drive.detail.downloadConfirm.title")}
                            </h2>
                            <div className="space-y-4 w-full">
                                <p className="text-tertiary">{t("drive.detail.downloadConfirm.message")}</p>
                                <div className="flex justify-end gap-2">
                                    <Button color="secondary" onClick={() => setShowDownloadModal(false)}>
                                        {t("drive.detail.downloadConfirm.cancel")}
                                    </Button>
                                    <Button onClick={confirmDownload}>
                                        {t("drive.detail.downloadConfirm.download")}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}
        </div>
    );
};

